// Round 36 — smart auto-position helper.
//
// Given the existing items in an InteractivePlugin box and a proposed
// position for a NEW item, return a non-overlapping position. Shifts on the
// y-axis in 10% steps until no overlap is detected against any existing
// item's [x, x+w] × [y, y+h] rectangle; wraps to a new column (x + 30%)
// when y would exceed 80%.
//
// Why a helper not the reducer: keeping it pure means we can unit-test
// without spinning up the redux store, and the reducer + library rail can
// both call it (e.g. drop-from-rail shares the smart placement logic).
//
// All positions are in percent of the parent canvas (0-100). Items lacking
// a `position` field are skipped (they're considered "unplaced"). The
// proposed position is returned verbatim when no overlap exists.

const STEP_Y = 10;
const STEP_X = 30;
const MAX_Y = 80;
const MAX_X = 80;

function rectsOverlap(a, b) {
  if (!a || !b) return false;
  if (a.x + a.w <= b.x) return false;
  if (b.x + b.w <= a.x) return false;
  if (a.y + a.h <= b.y) return false;
  if (b.y + b.h <= a.y) return false;
  return true;
}

function rectFor(item) {
  if (!item) return null;
  const p = item.position || (item.params && item.params.position);
  if (!p) return null;
  if (typeof p.x !== "number" || typeof p.y !== "number") return null;
  return {
    x: p.x,
    y: p.y,
    w: typeof p.w === "number" ? p.w : 30,
    h: typeof p.h === "number" ? p.h : 25,
  };
}

export function computeNonOverlappingPosition(existingItems, proposed) {
  if (!proposed || typeof proposed.x !== "number" || typeof proposed.y !== "number") {
    return proposed;
  }
  const rects = (existingItems || []).map(rectFor).filter(Boolean);
  if (rects.length === 0) return proposed;

  let candidate = {
    x: proposed.x,
    y: proposed.y,
    w: typeof proposed.w === "number" ? proposed.w : 30,
    h: typeof proposed.h === "number" ? proposed.h : 25,
  };

  // Try to find a non-overlapping slot. Step down by STEP_Y first; if y
  // exceeds MAX_Y, jump to the next column (x += STEP_X, y reset) and try
  // again. Cap at a few iterations so we never infinite-loop on a fully
  // packed canvas — the worst case just stacks at the last attempted slot.
  let iter = 0;
  while (rects.some((r) => rectsOverlap(candidate, r)) && iter < 30) {
    candidate = { ...candidate, y: candidate.y + STEP_Y };
    if (candidate.y > MAX_Y) {
      candidate = { ...candidate, x: candidate.x + STEP_X, y: proposed.y };
      if (candidate.x > MAX_X) {
        // Out of canvas — accept the last attempted slot and stop probing.
        break;
      }
    }
    iter++;
  }
  return candidate;
}
